'use client';
import { useEffect, useRef, useState } from 'react';
import type { WizardCtx } from '../types';
import { StepLayout, PrimaryBtn, GhostBtn, CameraIcon, SparkleIcon } from '../shared';
import { api, ApiError, USERNAME_REGEX, useAuth, toast } from '@splace/core';
const PHOTO_MAX_BYTES = 4 * 1024 * 1024;
const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif';

type UsernameState =
  | { status: 'idle' }
  | { status: 'invalid'; reason: string }
  | { status: 'checking' }
  | { status: 'available' }
  | { status: 'taken' }
  | { status: 'mine' };

function describeInvalid(value: string): string {
  if (value.length < 3) return 'at least 3 characters';
  if (value.length > 30) return 'at most 30 characters';
  if (/[A-Z]/.test(value)) return 'lowercase only';
  if (!/^[a-z0-9_]/.test(value)) return 'must start with letter, number or _';
  if (!/[a-z0-9_]$/.test(value)) return 'must end with letter, number or _';
  if (!/^[a-z0-9_.]+$/.test(value)) return 'only a–z, 0–9, _ and .';
  return 'invalid format';
}

export function ProfileStep({ ctx }: { ctx: WizardCtx }) {
  const user = useAuth(s => s.user);
  const setUser = useAuth(s => s.setUser);
  // Backend already created the user with sensible defaults — use them as the
  // starting values so the form doesn't look empty.
  const [name, setName] = useState(ctx.data.displayName || user?.displayName || '');
  const [username, setUsername] = useState(ctx.data.username || user?.username || '');
  const [bio, setBio] = useState(ctx.data.bio);
  const [usernameState, setUsernameState] = useState<UsernameState>({ status: 'idle' });
  const [loading, setLoading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function handlePhotoFile(file: File) {
    if (!PHOTO_ACCEPT.split(',').includes(file.type)) {
      toast.error('Use JPEG, PNG, WebP or HEIC');
      return;
    }
    if (file.size > PHOTO_MAX_BYTES) {
      toast.error('Image too large (max 4MB)');
      return;
    }
    setPhotoUploading(true);
    try {
      const updated = await api.users.uploadPhoto(file);
      setUser({ ...user!, photoUrl: updated.photoUrl, photoThumbUrl: updated.photoThumbUrl });
      toast.success('Photo updated');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Upload failed');
    } finally {
      setPhotoUploading(false);
    }
  }

  // Debounced live availability check.
  useEffect(() => {
    if (!username) { setUsernameState({ status: 'idle' }); return; }
    if (username === user?.username) { setUsernameState({ status: 'mine' }); return; }
    if (!USERNAME_REGEX.test(username)) {
      setUsernameState({ status: 'invalid', reason: describeInvalid(username) });
      return;
    }
    setUsernameState({ status: 'checking' });
    const handle = setTimeout(async () => {
      try {
        const { available } = await api.users.checkUsername(username);
        setUsernameState({ status: available ? 'available' : 'taken' });
      } catch {
        setUsernameState({ status: 'invalid', reason: 'check failed' });
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [username, user?.username]);

  const usernameOk =
    usernameState.status === 'available' || usernameState.status === 'mine';
  const canSave = !!name.trim() && usernameOk && !loading;

  async function save(opts: { skip?: boolean } = {}) {
    if (loading) return;
    if (!opts.skip && !canSave) return;
    setLoading(true);
    try {
      const body: Parameters<typeof api.users.update>[0] = {};
      const trimmedName = name.trim();
      if (trimmedName && trimmedName !== user?.displayName) body.displayName = trimmedName;
      if (username && username !== user?.username && usernameOk) body.username = username;
      const trimmedBio = bio.trim();
      if (trimmedBio !== (user?.bio ?? '')) body.bio = trimmedBio || undefined;

      if (Object.keys(body).length > 0) {
        const updated = await api.users.update(body);
        setUser({
          ...user!,
          username: updated.username,
          displayName: updated.displayName,
          photoUrl: updated.photoUrl,
        });
        ctx.update({
          username: updated.username,
          displayName: updated.displayName,
          bio: trimmedBio,
        });
      }
      ctx.next();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  }

  return (
    <StepLayout
      eyebrow="STEP 03 · PROFILE"
      title="Tell us about you"
      sub="Just the basics — you can update this any time from your profile."
    >
      <div className="ob-profile-grid">
        <div className="ob-form">
          <label className="ob-label">Display name</label>
          <input className="sp-input ob-input" value={name}
            onChange={e => setName(e.target.value)}
            placeholder="What people will call you" autoFocus
            disabled={loading} />

          <label className="ob-label">Username</label>
          <div className="ob-handle">
            <span className="ob-handle-prefix">@</span>
            <input
              className="ob-handle-input"
              value={username}
              onChange={e => setUsername(e.target.value.toLowerCase())}
              placeholder="your_handle"
              maxLength={30}
              autoComplete="off"
              spellCheck={false}
              disabled={loading}
            />
            <span className="ob-handle-status">
              <UsernameStatusBadge state={usernameState} />
            </span>
          </div>

          <label className="ob-label">Bio</label>
          <textarea className="sp-input ob-input ob-textarea" value={bio}
            onChange={e => setBio(e.target.value)}
            placeholder="A line or two about you." maxLength={160}
            disabled={loading} />
          <div className="ob-counter sp-mono">{bio.length}/160</div>

          <div className="ob-row" style={{ marginTop: 24 }}>
            <GhostBtn onClick={() => save({ skip: true })} disabled={loading}>Skip for now</GhostBtn>
            <PrimaryBtn onClick={() => save()} disabled={!canSave}>
              {loading ? 'Saving…' : 'Save and continue'}
            </PrimaryBtn>
          </div>
        </div>

        <div className="ob-avatar-side">
          <div className="ob-label">Photo</div>
          <input
            ref={fileInputRef}
            type="file"
            accept={PHOTO_ACCEPT}
            style={{ display: 'none' }}
            onChange={e => {
              const f = e.target.files?.[0];
              if (f) handlePhotoFile(f);
              e.target.value = '';
            }}
          />
          <button
            className="ob-avatar-picker"
            onClick={() => fileInputRef.current?.click()}
            disabled={photoUploading || loading}
          >
            {user?.photoUrl
              ? <img src={user.photoUrl} alt="" className="ob-avatar-preview" />
              : <><CameraIcon /><span>{photoUploading ? 'Uploading…' : 'Upload a photo'}</span></>}
          </button>
          <p className="ob-fineprint">JPG, PNG, WebP or HEIC, up to 4MB. Square works best.</p>
          <div className="ob-avatar-tip">
            <SparkleIcon />
            <div>
              <strong>Tip</strong>
              <p>Your real photo is only visible to people you link with.</p>
            </div>
          </div>
        </div>
      </div>
    </StepLayout>
  );
}

function UsernameStatusBadge({ state }: { state: UsernameState }) {
  switch (state.status) {
    case 'idle':      return null;
    case 'checking':  return <span className="ob-status ob-status--soft">checking…</span>;
    case 'available': return <span className="ob-status ob-status--ok">available</span>;
    case 'mine':      return <span className="ob-status ob-status--soft">your handle</span>;
    case 'taken':     return <span className="ob-status ob-status--bad">taken</span>;
    case 'invalid':   return <span className="ob-status ob-status--bad">{state.reason}</span>;
  }
}
